/**
 * Навигация по холсту, как в графических редакторах: зум и сдвиг вида в любой момент —
 * в том числе посреди цепочки стен, не обрывая её.
 *
 *   • Колесо мыши — зум к курсору, ступенькой.
 *   • Щипок на тачпаде (браузер шлёт его колесом с ctrlKey) и Ctrl/Cmd + колесо — плавный зум.
 *   • Два пальца по тачпаду — сдвиг вида.
 *   • Пробел + перетаскивание — сдвиг вида любым инструментом: точка при этом не ставится.
 *
 * Колесо слушаем нативно и без пассивного режима: React вешает его пассивно, а нам нужно
 * погасить действие браузера — иначе свайп двумя пальцами вбок листает историю «Назад».
 */
import { useEffect, useState, type RefObject } from "react";

/** Ступенька зума колесом мыши. */
const WHEEL_ZOOM_STEP = 1.12;
/** Чувствительность плавного зума щипком: множитель на пиксель прокрутки. */
const PINCH_ZOOM_RATE = 0.01;

/** Что сделать с событием колеса: сдвинуть вид на пиксели или зумнуть во столько раз. */
export type WheelGesture =
	| { kind: "pan"; dx: number; dy: number }
	| { kind: "zoom"; factor: number };

/**
 * Колесо мыши, щипок или два пальца по тачпаду. Отличаем по самому событию: щипок приходит
 * с ctrlKey, колесо мыши — строками или целыми щелчками строго по вертикали, а прокрутка
 * тачпадом — пикселями, дробно или со сдвигом вбок.
 */
export const wheelGesture = (e: WheelEvent): WheelGesture => {
	if (e.ctrlKey || e.metaKey) return { kind: "zoom", factor: Math.exp(e.deltaY * PINCH_ZOOM_RATE) };
	const mouseWheel =
		e.deltaMode !== WheelEvent.DOM_DELTA_PIXEL || (e.deltaX === 0 && Number.isInteger(e.deltaY));
	if (mouseWheel) {
		return { kind: "zoom", factor: e.deltaY > 0 ? WHEEL_ZOOM_STEP : 1 / WHEEL_ZOOM_STEP };
	}
	return { kind: "pan", dx: -e.deltaX, dy: -e.deltaY };
};

/** Фокус в поле ввода — пробел и стрелки там для текста, а не для холста. */
const isTyping = (target: EventTarget | null) => {
	const element = target as HTMLElement | null;
	return (
		!!element &&
		(["INPUT", "TEXTAREA", "SELECT"].includes(element.tagName) || element.isContentEditable)
	);
};

interface Params {
	svgRef: RefObject<SVGSVGElement | null>;
	zoomAt: (clientX: number, clientY: number, factor: number) => void;
	panByPixels: (dxPx: number, dyPx: number) => void;
}

export const useCanvasNavigation = ({ svgRef, zoomAt, panByPixels }: Params) => {
	/** Зажат пробел — перетаскивание двигает вид */
	const [spaceHeld, setSpaceHeld] = useState(false);

	useEffect(() => {
		const element = svgRef.current;
		if (!element) return;
		const onWheel = (e: WheelEvent) => {
			e.preventDefault();
			const gesture = wheelGesture(e);
			if (gesture.kind === "pan") panByPixels(gesture.dx, gesture.dy);
			else zoomAt(e.clientX, e.clientY, gesture.factor);
		};
		element.addEventListener("wheel", onWheel, { passive: false });
		return () => element.removeEventListener("wheel", onWheel);
	}, [svgRef, zoomAt, panByPixels]);

	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.code !== "Space" || isTyping(e.target)) return;
			// Без этого пробел нажал бы кнопку в фокусе или прокрутил страницу
			e.preventDefault();
			setSpaceHeld(true);
		};
		const onKeyUp = (e: KeyboardEvent) => {
			if (e.code !== "Space") return;
			if (!isTyping(e.target)) e.preventDefault();
			setSpaceHeld(false);
		};
		// Окно потеряло фокус с зажатым пробелом — keyup не придёт
		const onBlur = () => setSpaceHeld(false);
		window.addEventListener("keydown", onKeyDown);
		window.addEventListener("keyup", onKeyUp);
		window.addEventListener("blur", onBlur);
		return () => {
			window.removeEventListener("keydown", onKeyDown);
			window.removeEventListener("keyup", onKeyUp);
			window.removeEventListener("blur", onBlur);
		};
	}, []);

	return { spaceHeld };
};
